import DownloadIcon from '@mui/icons-material/Download'
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded'
import SearchIcon from '@mui/icons-material/Search'
import {
  Box,
  CircularProgress,
  InputAdornment,
  Link,
  OutlinedInput,
  styled,
  Typography
} from '@mui/material'
import dayjs from 'dayjs'
import { isEmpty } from 'lodash'
import {
  useEffect,
  useState,
  type Dispatch,
  type Key,
  type SetStateAction
} from 'react'

import { DatePicker, FormLabel, MButton } from 'components'
import { ADMIN_CONTACT_EMAIL, ROUTES_PATH } from 'config'
import { color } from 'config/color'
import { useAuth } from 'context'
import { useSnackbar } from 'notistack'
import { useLocation, useNavigate } from 'react-router-dom'
import { exportExcel, isAuthorized, useMobileBreakpoints } from 'utils'

interface ICampaignLogs {
  setActiveTab: Dispatch<SetStateAction<number>>
}

export const CampaignLogs = (props: ICampaignLogs): JSX.Element => {
  const currentDate = dayjs().format('YYYY-MM-DD')

  const [campaign, setCampaign] = useState<any>({})
  const [campaignId, setCampaignId] = useState<any>()
  const [initCampaignLogs, setInitCampaignLogs] = useState<any>([])
  const [campaignLogs, setCampaignLogs] = useState<any>([])
  const [search, setSearch] = useState('')
  const [startDate, setStartDate] = useState(
    dayjs(currentDate).subtract(1, 'days').format('YYYY-MM-DD')
  )
  const [endDate, setEndDate] = useState(currentDate)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const isMobile = useMobileBreakpoints()
  const navigate = useNavigate()
  const location = useLocation()
  const { enqueueSnackbar } = useSnackbar()

  const { getCampaignById, getCampaignLogsById, handleLogOut } = useAuth()

  const initPageDetails = async (campaignId: string): Promise<void> => {
    setIsLoading(true)
    try {
      const campaignResponse = await getCampaignById(campaignId)
      setCampaign(campaignResponse.campaign)

      const campaignLogsResponse = await getCampaignLogsById(
        campaignId,
        startDate,
        endDate
      )
      setCampaignLogs(campaignLogsResponse)
      setInitCampaignLogs(campaignLogsResponse)
    } catch (error: any) {
      if (!isAuthorized(error)) await handleLogOut()

      setCampaignLogs([])
      setInitCampaignLogs([])

      enqueueSnackbar(error?.message || 'Failed to fetch campaign logs', {
        variant: 'error',
        autoHideDuration: 3000
      })
    }

    setIsLoading(false)
  }

  const filterBySearch = (rawCampaignLogs: any[]): void => {
    setIsLoading(true)
    if (isEmpty(search)) {
      setCampaignLogs(rawCampaignLogs)
    } else {
      const filteredCampaignLogs = rawCampaignLogs.filter((cl: any) => {
        const personName = cl?.personName ?? ''
        return personName.toLowerCase().includes(search.toLowerCase())
      })

      setCampaignLogs(filteredCampaignLogs)
    }
    setIsLoading(false)
  }

  const handleSearchByDates = async (): Promise<void> => {
    try {
      setIsLoading(true)
      const campaignLogsResponse = await getCampaignLogsById(
        campaignId,
        startDate,
        endDate
      )

      setInitCampaignLogs(campaignLogsResponse)
      filterBySearch(campaignLogsResponse)
      setIsLoading(false)
    } catch (error) {
      if (!isAuthorized(error)) await handleLogOut()

      setInitCampaignLogs([])
      filterBySearch([])
      setIsLoading(false)
    }
  }

  const handleDownload = async (): Promise<void> => {
    const excelData = [
      [
        'accountId',
        'personName',
        'contactDetail',
        'referenceId',
        'notificationType',
        'error',
        'createdAt'
      ],
      ...(campaignLogs?.map((cl: any) => [
        cl.accountId,
        cl.personName,
        cl.contactDetail,
        cl.referenceId,
        cl.notificationType,
        cl.error,
        cl.createdAt
      ]) ?? [])
    ]
    exportExcel(
      excelData,
      'Campaign Logs',
      `campaign_logs_${startDate}-${endDate}.xlsx`
    )
  }

  useEffect(() => {
    filterBySearch(initCampaignLogs)
  }, [search])

  useEffect(() => {
    if (!isEmpty(location.search)) {
      const query = new URLSearchParams(location.search)

      const campaignId = query.get('id') ?? ''
      setCampaignId(campaignId)

      if (campaignId !== '') {
        void initPageDetails(campaignId)
      }
    }
  }, [])

  return (
    <Box>
      {!isEmpty(campaignId) && (
        <Box
          display="flex"
          p={3}
          gap={1}
          flexDirection={isMobile ? 'column' : 'row'}
          justifyContent="space-between"
          borderBottom={`solid 1px ${color.secondary.dark[200]}`}
        >
          <Box display="flex" gap={1} flexDirection="column">
            <Box display="flex" gap={1} flexDirection="row">
              <Typography
                color={color.grey[900]}
                fontWeight={400}
                fontSize={24}
              >
                {campaign.name}
              </Typography>

              <Typography
                color={color.grey[700]}
                fontWeight={400}
                fontSize={24}
              >
                Logs
              </Typography>
            </Box>

            <Typography color={color.grey[700]} fontWeight={400} fontSize={16}>
              {`${dayjs(campaign.createdAt).format('DD/MM/YYYY')}`}
            </Typography>
          </Box>

          <Box display="flex" gap={1} alignItems="center">
            <MButton
              variant="outlined"
              type="button"
              rounded="true"
              texttransform="none"
              onClick={() => {
                navigate(ROUTES_PATH.CAMPAIGNS)
                props.setActiveTab(0)
              }}
            >
              Go Back to Campaign Gallery
            </MButton>
          </Box>
        </Box>
      )}

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={isMobile ? 'none' : 'flex-end'}
        flexDirection={isMobile ? 'column' : 'row'}
        gap={3}
        p={3}
      >
        <Box display="flex" gap={2} flexDirection={isMobile ? 'column' : 'row'}>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
            gap={1}
          >
            <FormLabel>Search</FormLabel>

            <OutlinedInput
              name="search"
              onChange={(e: any) => {
                setSearch(e.target.value)
              }}
              placeholder="Search Log"
              endAdornment={
                <InputAdornment position="end">
                  <SearchIcon sx={{ color: color.primary[600] }} />
                </InputAdornment>
              }
              inputProps={{
                style: { color: color.grey[700] }
              }}
              fullWidth
            />
          </Box>

          <DatePicker
            label="from"
            value={startDate}
            onChange={(e: any) => {
              setStartDate(e.format('YYYY-MM-DD'))
            }}
            shouldDisableDate={(date) => date.isAfter(endDate)}
          />
          <DatePicker
            label="to"
            value={endDate}
            onChange={(e: any) => {
              setEndDate(e.format('YYYY-MM-DD'))
            }}
            shouldDisableDate={(date) => date.isBefore(startDate)}
          />
        </Box>

        <Box
          display="flex"
          flexDirection={isMobile ? 'column' : 'row'}
          gap={1}
          width={isMobile ? '100%' : 'auto'}
        >
          <MButton
            variant="outlined"
            type="button"
            size="large"
            rounded="true"
            texttransform="none"
            onClick={() => {
              setStartDate(
                dayjs(currentDate).subtract(1, 'days').format('YYYY-MM-DD')
              )
              setEndDate(currentDate)
            }}
          >
            Reset Dates
          </MButton>

          <MButton
            variant="contained"
            size="large"
            rounded="true"
            texttransform="none"
            onClick={() => {
              void handleSearchByDates()
            }}
          >
            Search Dates
          </MButton>
        </Box>
      </Box>

      {isLoading
        ? (
          <Box p={3} display="flex" justifyContent="center">
            <CircularProgress />
          </Box>
        )
        : (
          <Box>
            {campaignLogs.length > 0
              ? (
                <Box>
                  <Box px={3}>
                    {isMobile
                      ? (
                        <Box>
                          {campaignLogs.map(
                            (cl: any, index: Key | null | undefined) => {
                              return (
                                <CampaignLogsMobileView
                                  key={index}
                                  accountId={cl.accountId}
                                  personName={cl.personName}
                                  contactDetail={cl.contactDetail}
                                  referenceId={cl.referenceId}
                                  notificationType={cl.notificationType}
                                  error={cl.error}
                                  createdAt={cl.createdAt}
                                />
                              )
                            }
                          )}
                        </Box>
                      )
                      : (
                        <Box
                          border={1}
                          borderBottom={0}
                          borderColor={color.grey[200]}
                        >
                          <TableHeader>
                            <ItemWrapper width="10%">Account ID</ItemWrapper>
                            <ItemWrapper width="10%">Person Name</ItemWrapper>
                            <ItemWrapper width="10%">Contact Detail</ItemWrapper>
                            <ItemWrapper width="15%">Reference ID</ItemWrapper>
                            <ItemWrapper width="10%">Notification Type</ItemWrapper>
                            <ItemWrapper width="35%">Error</ItemWrapper>
                            <ItemWrapper width="10%">Created At</ItemWrapper>
                          </TableHeader>
                          <Box>
                            {campaignLogs.map(
                              (cl: any, index: Key | null | undefined) => {
                                return (
                                  <TableItem key={index}>
                                    <ItemWrapper width="10%" small="true">
                                      {cl?.accountId}
                                    </ItemWrapper>
                                    <ItemWrapper width="10%" small="true">
                                      {cl?.personName}
                                    </ItemWrapper>
                                    <ItemWrapper width="10%" small="true">
                                      {cl?.contactDetail}
                                    </ItemWrapper>
                                    <ItemWrapper width="15%" small="true">
                                      {cl?.referenceId}
                                    </ItemWrapper>
                                    <ItemWrapper width="10%" small="true">
                                      {cl?.notificationType}
                                    </ItemWrapper>
                                    <ItemWrapper width="35%" small="true">
                                      {cl?.error}
                                    </ItemWrapper>
                                    <ItemWrapper width="10%" small="true">
                                      {cl?.createdAt}
                                    </ItemWrapper>
                                  </TableItem>
                                )
                              }
                            )}
                          </Box>
                        </Box>
                      )}
                  </Box>
                </Box>
              )
              : (
                <Box>
                  <Box px={3}>
                    {isMobile
                      ? (
                        <Box display="flex" justifyContent="center">
                          <Typography
                            color={color.grey[900]}
                            fontWeight={500}
                            fontSize={18}
                          >
                            No results found
                          </Typography>
                        </Box>
                      )
                      : (
                        <Box border={1} borderColor={color.grey[200]}>
                          <TableHeader>
                            <ItemWrapper width="10%">Account ID</ItemWrapper>
                            <ItemWrapper width="10%">Person Name</ItemWrapper>
                            <ItemWrapper width="10%">Contact Detail</ItemWrapper>
                            <ItemWrapper width="10%">Reference ID</ItemWrapper>
                            <ItemWrapper width="10%">Notification Type</ItemWrapper>
                            <ItemWrapper width="35%">Error</ItemWrapper>
                            <ItemWrapper width="15%">Created At</ItemWrapper>
                          </TableHeader>
                          <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            p={10}
                          >
                            <Box
                              display="flex"
                              flexDirection="column"
                              alignItems="center"
                              width="35%"
                              gap={1}
                            >
                              <ErrorRoundedIcon
                                sx={{
                                  color: color.grey[500],
                                  backgroundColor: color.grey[100],
                                  fontSize: 50,
                                  borderRadius: '50%',
                                  padding: 3
                                }}
                              />
                              <Typography
                                color={color.grey[900]}
                                fontWeight={500}
                                fontSize={24}
                              >
                                No logs to view
                              </Typography>

                              <Typography
                                color={color.grey[700]}
                                fontWeight={400}
                                fontSize={16}
                              >
                                This campaign still doesn’t have any log to preview,
                                please comeback later. If you think this is an error
                                please
                                <Link
                                  ml={0.5}
                                  href={`mailto:${ADMIN_CONTACT_EMAIL}`}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  <Typography
                                    component="span"
                                    sx={{ textDecoration: 'underline' }}
                                    color={color.primary[600]}
                                    fontWeight={400}
                                    fontSize={16}
                                  >
                                    create a Jira ticket
                                  </Typography>
                                </Link>
                                {
                                  ' and our support team will start working on a resolution.'
                                }
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      )}
                  </Box>
                </Box>
              )}

            <Box p={3}>
              <MButton
                variant="outlined"
                type="submit"
                size="large"
                rounded="true"
                texttransform="none"
                sx={{ fontSize: 16 }}
                onClick={handleDownload}
                startIcon={<DownloadIcon />}
                disabled={isEmpty(campaignLogs)}
                fullWidth={isMobile}
              >
                Download CSV File
              </MButton>
            </Box>
          </Box>
        )}
    </Box>
  )
}

interface ICampaignLogsMobileView {
  accountId: string
  personName: string
  contactDetail: string
  referenceId: string
  notificationType: string
  error: string
  createdAt: string
}

const CampaignLogsMobileView = (
  props: ICampaignLogsMobileView
): JSX.Element => {
  return (
    <Box py={2} borderBottom={`solid 1px ${color.secondary.dark[100]}`}>
      <Box display="flex" justifyContent="space-between">
        <Typography fontSize={14} color={color.grey[700]}>
          Account ID
        </Typography>
        <Typography fontSize={14} color={color.grey[900]} fontWeight={700}>
          {props?.accountId}
        </Typography>
      </Box>
      <Box display="flex" justifyContent="space-between">
        <Typography fontSize={14} color={color.grey[700]}>
          Person Name
        </Typography>
        <Typography fontSize={14} color={color.grey[900]} fontWeight={700}>
          {props?.personName}
        </Typography>
      </Box>
      <Box display="flex" justifyContent="space-between">
        <Typography fontSize={14} color={color.grey[700]}>
          Contact Detail
        </Typography>
        <Typography fontSize={14} color={color.grey[900]} fontWeight={700}>
          {props?.contactDetail}
        </Typography>
      </Box>
      <Box display="flex" justifyContent="space-between" flexWrap="wrap">
        <Typography fontSize={14} color={color.grey[700]}>
          Reference ID
        </Typography>
        <Typography fontSize={14} color={color.grey[900]} fontWeight={700}>
          {props?.referenceId}
        </Typography>
      </Box>
      <Box display="flex" justifyContent="space-between">
        <Typography fontSize={14} color={color.grey[700]}>
          Notification Type
        </Typography>
        <Typography fontSize={14} color={color.grey[900]} fontWeight={700}>
          {props?.notificationType}
        </Typography>
      </Box>
      <Box display="flex" justifyContent="space-between" flexWrap="wrap">
        <Typography fontSize={14} color={color.grey[700]}>
          Error
        </Typography>
        <Typography fontSize={14} color={color.grey[900]} fontWeight={700}>
          {props?.error}
        </Typography>
      </Box>
      <Box display="flex" justifyContent="space-between">
        <Typography fontSize={14} color={color.grey[700]}>
          Created At
        </Typography>
        <Typography fontSize={14} color={color.grey[900]} fontWeight={700}>
          {props?.createdAt}
        </Typography>
      </Box>
    </Box>
  )
}

export const TableHeader = styled(Box)({
  display: 'flex',
  background: color.grey[200]
})

export const ItemWrapper = styled(Box)<{ small?: string, width?: string }>(
  ({ small, width }) => ({
    width,
    padding: 15,
    color: color.black,
    fontSize: small === 'true' ? '14px' : '16px',
    fontWeight: small === 'true' ? '400' : 'bold',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'wrap',
    wordWrap: 'break-word',
    wordBreak: 'break-all'
  })
)

export const TableItem = styled(Box)({
  display: 'flex',
  borderBottom: '1px solid #eee',
  '&:hover': {
    backgroundColor: color.primary[50]
  }
})
